interface SearchFormData {
  project: string;
  originator: string;
  volume: string;
  level: string;
  documentType: string;
  role: string;
  classification: string;
  description: string;
}

interface SearchResult {
  projectCode: string;
  resourceGuid: string;
  project: string;
  originator: string;
  volume: string;
  level: string;
  documentType: string;
  role: string;
  classification: string;
  documentNumber: number;
  suitabilityStatus: string;
  revision: string;
  fileName: string | null;
  fileExtension: string | null;
  folderPath: string | null;
  navigationPaths: any[];
}

interface SearchState {
  formData: SearchFormData;
  searchResults: SearchResult[];
  timestamp: number;
}

const SEARCH_STATE_PREFIX = 'search_state_';
const STATE_EXPIRY_HOURS = 24;

export const searchStateUtils = {
  getStorageKey(projectCode: string): string {
    return `${SEARCH_STATE_PREFIX}${projectCode}`;
  },

  saveSearchState(projectCode: string, formData: SearchFormData, searchResults: SearchResult[]): void {
    if (typeof window === 'undefined') return;
    
    const state: SearchState = {
      formData,
      searchResults,
      timestamp: Date.now()
    };
    
    try {
      const key = this.getStorageKey(projectCode);
      localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save search state:', error);
    }
  },

  loadSearchState(projectCode: string): SearchState | null {
    if (typeof window === 'undefined') return null;
    
    try {
      const key = this.getStorageKey(projectCode);
      const stored = localStorage.getItem(key);
      
      if (!stored) return null;
      
      const state: SearchState = JSON.parse(stored);
      
      // Check if state is expired
      const isExpired = Date.now() - state.timestamp > (STATE_EXPIRY_HOURS * 60 * 60 * 1000);
      if (isExpired) {
        this.clearSearchState(projectCode);
        return null;
      }
      
      return state;
    } catch (error) {
      console.warn('Failed to load search state:', error);
      return null;
    }
  },

  clearSearchState(projectCode: string): void {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.removeItem(this.getStorageKey(projectCode));
    } catch (error) {
      console.warn('Failed to clear search state:', error);
    }
  },

  clearAllSearchStates(): void {
    if (typeof window === 'undefined') return;
    
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(SEARCH_STATE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear all search states:', error);
    }
  },

  getDefaultFormData(projectCode: string = ''): SearchFormData {
    return {
      project: projectCode,
      originator: "All",
      volume: "All",
      level: "All",
      documentType: "All",
      role: "All",
      classification: "All",
      description: "",
    };
  }
};